import React from "react";
import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import { Icon, IconBox } from "@proda-ai/ui-kit";

export type LayoutFullScreenWithCollapsibleSidebarProps = BoxProps & {
    HeaderProps?: BoxProps;
    MainContentProps?: BoxProps;
    SidebarProps?: BoxProps;
    FooterProps?: BoxProps;
    headerText: string;
    headerLeftMenu?: React.ReactNode;
    sidebarHeader: string;
    topbarLeftAction?: React.ReactNode;
    topbarRightContent?: React.ReactNode;
    sidebarContent?: React.ReactNode;
    footerContent?: React.ReactNode;
    isSidebarCollapsed?: boolean;
    onClose?: () => void;
    onSidebarToggle?: () => void;
};

export const testIds = {
    main: "LayoutFullScreenWithCollapsibleSidebar",
    iconClose: "LayoutFullScreenWithCollapsibleSidebar-iconClose",
    header: "LayoutFullScreenWithCollapsibleSidebar-header",
    sidebar: "LayoutFullScreenWithCollapsibleSidebar-sidebar",
};
export const SIDEBAR_WIDTH = "421.89px";
const LayoutFullScreenWithCollapsibleSidebar = (
    props: LayoutFullScreenWithCollapsibleSidebarProps,
) => {
    const {
        sx,
        children,
        headerText,
        headerLeftMenu,
        sidebarHeader,
        topbarLeftAction,
        topbarRightContent,
        sidebarContent,
        footerContent,
        HeaderProps,
        MainContentProps,
        SidebarProps,
        FooterProps,
        isSidebarCollapsed = false,
        onClose,
        onSidebarToggle,
        ...otherProps
    } = props;

    return (
        <Box
            data-testid={testIds.main}
            className={testIds.main}
            sx={{
                width: "100vw",
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: "var(--proda-white)",
                boxSizing: "border-box",
                ...sx,
            }}
            {...otherProps}
        >
            <Box
                {...HeaderProps}
                sx={{
                    height: "60px",
                    borderBottom: "1px solid var(--proda-neon-blue-10)",
                    flexShrink: 0,
                    backgroundColor: "var(--proda-white)",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    ...HeaderProps?.sx,
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        left: "16px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                    }}
                >
                    {topbarLeftAction}
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flex: 1,
                        overflow: "visible",
                        boxSizing: "border-box",
                        position: "relative",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {headerLeftMenu !== undefined && (
                        <Box
                            sx={{
                                margin: "0 16px",
                                position: "relative",
                                zIndex: 2,
                            }}
                        >
                            {headerLeftMenu}
                        </Box>
                    )}
                    <Box
                        component="h1"
                        sx={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: "20px",
                            fontWeight: 700,
                            lineHeight: "24px",
                            color: "var(--proda-black)",
                            margin: 0,
                            textAlign: "center",
                        }}
                        data-testid={testIds.header}
                    >
                        {headerText}
                    </Box>
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        right: "16px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                    }}
                >
                    {topbarRightContent}
                    {onClose && (
                        <IconBox
                            style={{ cursor: "pointer" }}
                            data-testid={testIds.iconClose}
                            onClick={onClose}
                        >
                            <Icon icon="xmark" color="var(--proda-black)" />
                        </IconBox>
                    )}
                </Box>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flex: 1,
                    overflow: "hidden",
                    boxSizing: "border-box",
                }}
            >
                <Box
                    {...SidebarProps}
                    sx={{
                        width: isSidebarCollapsed ? "60px" : SIDEBAR_WIDTH,
                        transition: "width 0.3s ease",
                        borderRight: "1px solid var(--proda-white-130)",
                        overflowY: "auto",
                        overflowX: "hidden",
                        flexShrink: 0,
                        backgroundColor: "var(--proda-white)",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        ...SidebarProps?.sx,
                    }}
                    data-testid={testIds.sidebar}
                >
                    <Box
                        sx={{
                            padding: "24px 24px 16px 24px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "8px",
                            }}
                        >
                            {!isSidebarCollapsed && (
                                <Box
                                    component="h2"
                                    sx={{
                                        fontFamily: "'DM Sans', sans-serif",
                                        fontSize: "16px",
                                        fontWeight: 700,
                                        lineHeight: "24px",
                                        color: "var(--proda-black)",
                                        margin: 0,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {sidebarHeader}
                                </Box>
                            )}
                            {onSidebarToggle ? (
                                <Box
                                    onClick={onSidebarToggle}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <IconBox
                                        style={{
                                            transform: isSidebarCollapsed
                                                ? "rotate(180deg)"
                                                : "rotate(0deg)",
                                        }}
                                    >
                                        <Icon
                                            icon="angle-left"
                                            color="var(--proda-black)"
                                        />
                                    </IconBox>
                                </Box>
                            ) : (
                                <Box
                                    onClick={onSidebarToggle}
                                    sx={{ height: "21px" }}
                                />
                            )}
                        </Box>
                    </Box>
                    {!isSidebarCollapsed && sidebarContent}
                </Box>
                <Box
                    {...MainContentProps}
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        backgroundColor: "var(--proda-white)",
                        boxSizing: "border-box",
                        ...MainContentProps?.sx,
                    }}
                >
                    {children}
                </Box>
            </Box>
            <Box
                {...FooterProps}
                sx={{
                    height: "113px",
                    borderTop: "2px solid var(--proda-neon-blue-10)",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexShrink: 0,
                    backgroundColor: "var(--proda-white)",
                    boxSizing: "border-box",
                    ...FooterProps?.sx,
                }}
            >
                {footerContent}
            </Box>
        </Box>
    );
};

export default LayoutFullScreenWithCollapsibleSidebar;
